import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import LanguageSelector from '../LanguageSelector/LanguageSelector'
import { useLanguage } from '../../hooks/useLanguage'
import api from '../../services/api'

const Header: React.FC = () => {
  const location = useLocation()
  const { user } = useAuth()
  const { t } = useLanguage()
  const isHomePage = location.pathname === '/'
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [cartItemCount, setCartItemCount] = useState(0)
  const [hasActiveProducts, setHasActiveProducts] = useState(false)
  
  // Force re-render when auth state changes
  const [, forceUpdate] = useState({})
  
  useEffect(() => {
    const handleAuthChange = () => {
      forceUpdate({})
    }
    
    window.addEventListener('customAuthStateChange', handleAuthChange)
    return () => window.removeEventListener('customAuthStateChange', handleAuthChange)
  }, [])

  // Check if there are active products
  useEffect(() => {
    const checkActiveProducts = async () => {
      try {
        const response = await api.get('/products')
        const products = response.data || []
        setHasActiveProducts(products.length > 0)
      } catch (error) {
        console.error('Error checking products:', error)
        setHasActiveProducts(false)
      }
    }

    checkActiveProducts()
  }, [])

  // Fetch cart item count when user is logged in
  useEffect(() => {
    if (user) {
      fetchCartItemCount()
    } else {
      setCartItemCount(0)
    }
  }, [user])

  // Listen for cart updates
  useEffect(() => {
    const handleCartUpdate = () => {
      if (user) {
        fetchCartItemCount()
      }
    }
    
    window.addEventListener('cartUpdated', handleCartUpdate)
    return () => window.removeEventListener('cartUpdated', handleCartUpdate)
  }, [user])

  const fetchCartItemCount = async () => {
    try {
      const response = await api.get('/cart')
      const cart = response.data
      const totalItems = cart?.items?.reduce((sum: number, item: any) => sum + item.quantity, 0) || 0
      setCartItemCount(totalItems)
    } catch (error) {
      console.error('Error fetching cart:', error)
      setCartItemCount(0)
    }
  }

  const handleTripsClick = (e: React.MouseEvent) => {
    if (isHomePage) {
      // Se siamo sulla home page, scrolla alla sezione trips
      e.preventDefault()
      const tripsSection = document.getElementById('trips')
      if (tripsSection) {
        const headerHeight = 80 // Altezza approssimativa dell'header
        const elementPosition = tripsSection.offsetTop - headerHeight
        
        window.scrollTo({
          top: elementPosition,
          behavior: 'smooth'
        })
      }
    }
    // Chiudi il menu mobile dopo il click
    setIsMenuOpen(false)
  }

  const toggleMenu = () => {
    console.log('Toggle menu clicked, current state:', isMenuOpen)
    setIsMenuOpen(!isMenuOpen)
  }

  const closeMenu = () => {
    setIsMenuOpen(false)
  }



  return (
    <header className="site-header bg-blue-400 text-white w-full relative z-50">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-2 flex justify-between items-center">
        {/* Logo */}
        <div className="site-branding flex items-center">
          <Link to="/" className="custom-logo-link flex items-center">
            <img 
              width="71" 
              height="75" 
              src="/logo.ico" 
              className="custom-logo mr-2 sm:mr-3 w-14 h-14 sm:w-16 sm:h-16 max-w-16 max-h-16" 
              alt="La Dolce Vita" 
            />
            <div className="text-logo">
              <h1 className="site-title text-lg sm:text-xl lg:text-2xl font-bold tracking-wide">
                <span className="font-serif italic">La Dolce Vita Reis</span>
              </h1>
            </div>
          </Link>
        </div>

        {/* Desktop Navigation */}
        <div className="navigation-container hidden md:block">
          <nav className="main-navigation">
            <ul className="menu flex space-x-4 sm:space-x-6 lg:space-x-8 text-sm sm:text-base items-center">
              <li className="menu-item">
                {isHomePage ? (
                  <a 
                    href="#trips" 
                    onClick={handleTripsClick}
                    className="hover:text-yellow-300 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {t('nav.trips')}
                  </a>
                ) : (
                  <Link 
                    to="/#trips" 
                    onClick={handleTripsClick}
                    className="hover:text-yellow-300 transition-colors whitespace-nowrap"
                  >
                    {t('nav.trips')}
                  </Link>
                )}
              </li>
              {hasActiveProducts && (
                <li className="menu-item">
                  <Link to="/products" className="hover:text-yellow-300 transition-colors whitespace-nowrap uppercase">{t('nav.products')}</Link>
                </li>
              )}
              <li className="menu-item">
                <Link to="/about-us" className="hover:text-yellow-300 transition-colors whitespace-nowrap uppercase">{t('nav.aboutUs')}</Link>
              </li>
              <li className="menu-item">
                <Link to="/contact" className="hover:text-yellow-300 transition-colors whitespace-nowrap uppercase">{t('nav.contacts')}</Link>
              </li>
              {user ? (
                <>
                  {hasActiveProducts && (
                    <li className="menu-item">
                      <Link to="/cart" className="relative hover:text-yellow-300 transition-colors whitespace-nowrap flex items-center uppercase">
                        <svg className="w-6 h-6 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                        </svg>
                        {cartItemCount > 0 && (
                          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                            {cartItemCount > 99 ? '99+' : cartItemCount}
                          </span>
                        )}
                        {t('nav.cart') || 'Cart'}
                      </Link>
                    </li>
                  )}
                  <li className="menu-item">
                    <Link to="/account" className="hover:text-yellow-300 transition-colors whitespace-nowrap uppercase">{t('nav.myAccount')}</Link>
                  </li>
                </>
              ) : (
                <>
                  <li className="menu-item">
                    <Link to="/login" className="hover:text-yellow-300 transition-colors whitespace-nowrap uppercase">{t('nav.login')}</Link>
                  </li>
                  <li className="menu-item">
                    <Link to="/signup" className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-full transition-colors whitespace-nowrap uppercase">{t('nav.signUp')}</Link>
                  </li>
                </>
              )}
              <li className="menu-item">
                <LanguageSelector />
              </li>
            </ul>
          </nav>
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden">
          <button
            onClick={toggleMenu}
            className="text-white hover:text-yellow-300 transition-colors focus:outline-none p-2"
            aria-label="Toggle menu"
          >
            <div className="w-6 h-6 flex flex-col justify-center items-center">
              <span className={`block w-5 h-0.5 bg-current transform transition duration-300 ease-in-out ${isMenuOpen ? 'rotate-45 translate-y-1' : '-translate-y-1'}`}></span>
              <span className={`block w-5 h-0.5 bg-current transition duration-300 ease-in-out ${isMenuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
              <span className={`block w-5 h-0.5 bg-current transform transition duration-300 ease-in-out ${isMenuOpen ? '-rotate-45 -translate-y-1' : 'translate-y-1'}`}></span>
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div className="md:hidden absolute top-full left-0 w-full bg-blue-500 shadow-lg z-40">
          <nav className="mobile-navigation py-4">
            <ul className="menu flex flex-col space-y-4 px-4">
              <li className="menu-item">
                {isHomePage ? (
                  <a 
                    href="#trips" 
                    onClick={handleTripsClick}
                    className="block py-2 hover:text-yellow-300 transition-colors cursor-pointer text-lg font-medium uppercase"
                  >
                    {t('nav.trips')}
                  </a>
                ) : (
                  <Link 
                    to="/#trips" 
                    onClick={handleTripsClick}
                    className="block py-2 hover:text-yellow-300 transition-colors text-lg font-medium uppercase"
                  >
                    {t('nav.trips')}
                  </Link>
                )}
              </li>
              {hasActiveProducts && (
                <li className="menu-item">
                  <Link 
                    to="/products" 
                    onClick={closeMenu}
                    className="block py-2 hover:text-yellow-300 transition-colors text-lg font-medium uppercase"
                  >
                    {t('nav.products')}
                  </Link>
                </li>
              )}
              <li className="menu-item">
                <Link 
                  to="/about-us" 
                  onClick={closeMenu}
                  className="block py-2 hover:text-yellow-300 transition-colors text-lg font-medium uppercase"
                >
                  {t('nav.aboutUs')}
                </Link>
              </li>
              <li className="menu-item">
                <Link 
                  to="/contact" 
                  onClick={closeMenu}
                  className="block py-2 hover:text-yellow-300 transition-colors text-lg font-medium uppercase"
                >
                  {t('nav.contacts')}
                </Link>
              </li>
              {user ? (
                <>
                  {hasActiveProducts && (
                    <li className="menu-item">
                      <Link 
                        to="/cart" 
                        onClick={closeMenu}
                        className="block py-2 hover:text-yellow-300 transition-colors text-lg font-medium flex items-center uppercase"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                        </svg>
                        {t('nav.cart') || 'Cart'}
                        {cartItemCount > 0 && (
                          <span className="ml-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                            {cartItemCount > 99 ? '99+' : cartItemCount}
                          </span>
                        )}
                      </Link>
                    </li>
                  )}
                  <li className="menu-item">
                    <Link 
                      to="/account" 
                      onClick={closeMenu}
                      className="block py-2 hover:text-yellow-300 transition-colors text-lg font-medium uppercase"
                    >
                      {t('nav.myAccount')}
                    </Link>
                  </li>
                </>
              ) : (
                <>
                  <li className="menu-item">
                    <Link 
                      to="/login" 
                      onClick={closeMenu}
                      className="block py-2 hover:text-yellow-300 transition-colors text-lg font-medium uppercase"
                    >
                      {t('nav.login')}
                    </Link>
                  </li>
                  <li className="menu-item">
                    <Link 
                      to="/signup" 
                      onClick={closeMenu}
                      className="block py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg transition-colors text-lg font-medium uppercase"
                    >
                      {t('nav.signUp')}
                    </Link>
                  </li>
                </>
              )}
              <li className="menu-item">
                <LanguageSelector />
              </li>
            </ul>
          </nav>
        </div>
      )}
    </header>
  )
}

export default Header 